import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import WebSocketService from '../services/websocketService';
import { AuthContext } from './authContext';
import { useCompany } from './companyContext';

const WebSocketContext = createContext(null);

const resolveWsUrl = () => {
  // 1. Explicit WS env var wins
  if (process.env.REACT_APP_WS_URL) return process.env.REACT_APP_WS_URL;

  // 2. Derive from REACT_APP_BACKEND_URL (http→ws, https→wss)
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (backend) {
    const url = new URL(backend);
    const wsProto = url.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProto}://${url.host}/ws`;
  }

  // 3. Development fallback: CRA runs on 3000/3001, backend WS usually runs on 4000.
  // Do not use window.location.host here because that points to the frontend server
  // (e.g. ws://192.168.1.121/ws), which has no backend WebSocket route.
  if (process.env.NODE_ENV === 'development') {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${protocol}://${window.location.hostname}:4000/ws`;
  }

  // 4. Production fallback: derive from current page origin (reverse proxy / same-origin deploy)
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${window.location.host}/ws`;
};

export const useWebSocketClient = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketClient must be used within a WebSocketProvider');
  }
  return context;
};

/**
 * Safe version — returns null instead of throwing when called outside WebSocketProvider.
 * Use this in components that may render before the provider is mounted.
 */
export const useWebSocketClientSafe = () => {
  return useContext(WebSocketContext);
};

export const WebSocketProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const { companyData } = useCompany();

  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [contactUpdates, setContactUpdates] = useState([]);
  const [refundUpdates, setRefundUpdates] = useState([]);

  // ── Phase 1 additions ──
  const [transactionalStatusUpdates, setTransactionalStatusUpdates] = useState([]);
  const [audienceFetchUpdates, setAudienceFetchUpdates] = useState([]);
  const [webhookDeliveryUpdates, setWebhookDeliveryUpdates] = useState([]);
  const [webhookSubscriptionUpdates, setWebhookSubscriptionUpdates] = useState([]);
  const [externalInboxMessages, setExternalInboxMessages] = useState([]);

  const wsUrl = useMemo(resolveWsUrl, []);
  const userId = useMemo(
    () => currentUser?.name || currentUser?.companyid || null,
    [currentUser]
  );
  const companyId = useMemo(
    () => currentUser?.companyid || companyData?.customeruuid || sessionStorage.getItem('customeruuid') || null,
    [currentUser, companyData]
  );

  useEffect(() => {
    if (!userId || !companyId) return undefined;

    const handleMessage = (payload) => setMessages((prev) => [...prev, payload]);
    const handleStatus = (payload) => setStatusUpdates((prev) => [...prev, payload]);
    const handleContactUpdate = (payload) => setContactUpdates((prev) => [...prev, payload]);
    const handleRefundUpdate = (payload) => setRefundUpdates((prev) => [...prev, payload]);

    // ── Phase 1 additions ──
    const handleTxStatus = (payload) => setTransactionalStatusUpdates((prev) => [...prev, payload]);
    const handleAudienceFetch = (payload) => setAudienceFetchUpdates((prev) => [...prev, payload]);
    const handleWebhookDelivery = (payload) => setWebhookDeliveryUpdates((prev) => [...prev, payload]);
    const handleWebhookSubscription = (payload) => setWebhookSubscriptionUpdates((prev) => [...prev, payload]);
    const handleExternalInbox = (payload) => setExternalInboxMessages((prev) => [...prev, payload]);

    WebSocketService.onConnectionChange(setIsConnected);
    WebSocketService.onMessage(handleMessage);
    WebSocketService.onStatusUpdate(handleStatus);
    WebSocketService.onContactUpdate(handleContactUpdate);
    WebSocketService.onRefundUpdate(handleRefundUpdate);
    WebSocketService.onTransactionalStatus(handleTxStatus);
    WebSocketService.onAudienceFetch(handleAudienceFetch);
    WebSocketService.onWebhookDelivery(handleWebhookDelivery);
    WebSocketService.onWebhookSubscription(handleWebhookSubscription);
    WebSocketService.onExternalInbox(handleExternalInbox);

    WebSocketService.connect(userId, companyId, wsUrl).catch((error) => {
      console.error('WebSocket: initial connection failed', error);
    });

    return () => {
      WebSocketService.clearHandlers();
      WebSocketService.disconnect();
      setMessages([]);
      setStatusUpdates([]);
      setContactUpdates([]);
      setRefundUpdates([]);
      setTransactionalStatusUpdates([]);
      setAudienceFetchUpdates([]);
      setWebhookDeliveryUpdates([]);
      setWebhookSubscriptionUpdates([]);
      setExternalInboxMessages([]);
      setIsConnected(false);
    };
  }, [userId, companyId, wsUrl]);

  const subscribeToConversation = React.useCallback(
    (conversationId) => WebSocketService.subscribeToConversation(conversationId),
    []
  );

  const unsubscribeFromConversation = React.useCallback(
    (conversationId) => WebSocketService.unsubscribeFromConversation(conversationId),
    []
  );

  const send = React.useCallback((payload) => WebSocketService.send(payload), []);

  const reconnect = React.useCallback(
    () => (userId && companyId ? WebSocketService.connect(userId, companyId, wsUrl) : Promise.resolve()),
    [userId, companyId, wsUrl]
  );

  const markAsRead = React.useCallback(
    (phoneNumber) => WebSocketService.markAsRead(phoneNumber),
    []
  );

  const connectionInfo = useMemo(() => WebSocketService.getStatus(), [isConnected]);

  const value = useMemo(
    () => ({
      isConnected,
      messages,
      statusUpdates,
      contactUpdates,
      refundUpdates,
      transactionalStatusUpdates,
      audienceFetchUpdates,
      webhookDeliveryUpdates,
      webhookSubscriptionUpdates,
      externalInboxMessages,
      subscribeToConversation,
      unsubscribeFromConversation,
      send,
      reconnect,
      markAsRead,
      connectionInfo
    }),
    [connectionInfo, contactUpdates, isConnected, markAsRead, messages, reconnect, refundUpdates, send, statusUpdates, subscribeToConversation, unsubscribeFromConversation, transactionalStatusUpdates, audienceFetchUpdates, webhookDeliveryUpdates, webhookSubscriptionUpdates, externalInboxMessages]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
